import {useCallback} from 'react'
import {getIncomers, getOutgoers, Node, Edge} from 'reactflow'
import {NodeType, EDGE_TYPES} from '../../../../../../Utils/Constant/FlowConstants'
import {setFlowDraft} from '../../../../../../Redux/FrontEnd/FlowDraftSlice'
import {
  deleteConditionBranch,
  getConditionDeleteOptions,
  BranchType,
} from './useConditionNodeDelete'

interface UseFlowNodeDeleteArgs {
  nodes: Node[]
  edges: Edge[]
  setNodes: (nodes: Node[]) => void
  setEdges: (edges: Edge[]) => void
  setNodesProgrammatic?: (nodes: Node[]) => void
  setEdgesProgrammatic?: (edges: Edge[]) => void
  dispatch?: any
  flowName?: string
  flowStatus?: string
  pushToUndoStack?: (nodes: Node[], edges: Edge[]) => void
  isUndoRedoOperation?: React.MutableRefObject<boolean>
}

export const deleteNodesLogic = (
  deleted: Node[],
  nodes: Node[],
  edges: Edge[]
): {updatedNodes: Node[]; updatedEdges: Edge[]} => {
  let updatedEdges = [...edges]
  let updatedNodes = nodes.filter((n) => !deleted.some((d) => d.id === n.id))

  deleted.forEach((node) => {
    const incomers = getIncomers(node, nodes, edges)
    const outgoers = getOutgoers(node, nodes, edges)

    if (node.type === NodeType.CONDITION) {
      const outgoingEdges = edges.filter((e) => e.source === node.id)
      let removedTargets: string[] = []

      if (outgoingEdges.length > 0) {
        const targetMap: Record<string, {edges: typeof outgoingEdges; isRootEnd: boolean}> = {}

        outgoingEdges.forEach((edge) => {
          const targetNode = nodes.find((n) => n.id === edge.target)
          if (!targetMap[edge.target]) {
            targetMap[edge.target] = {edges: [], isRootEnd: false}
          }
          targetMap[edge.target].edges.push(edge)
          if (targetNode && targetNode.type === NodeType.END && targetNode.deletable === false) {
            targetMap[edge.target].isRootEnd = true
          }
        })
        Object.entries(targetMap).forEach(([targetId, {edges: edgeList, isRootEnd}]) => {
          if (!isRootEnd) {
            edgeList.forEach((edge) => {
              updatedEdges = updatedEdges.filter((e) => e.id !== edge.id)
            })
            removedTargets.push(targetId)
          }
        })
      }

      updatedEdges = updatedEdges.filter(
        (edge) => edge.source !== node.id && edge.target !== node.id
      )

      const remainingOutgoers = outgoers.filter((outNode) => !removedTargets.includes(outNode.id))
      remainingOutgoers.forEach((outNode) => {
        incomers.forEach((inNode) => {
          const alreadyConnected = updatedEdges.some(
            (e) => e.source === inNode.id && e.target === outNode.id
          )
          if (!alreadyConnected) {
            updatedEdges.push({
              id: `${inNode.id}->${outNode.id}`,
              source: inNode.id,
              target: outNode.id,
              type: EDGE_TYPES.SMOOTHSTEP,
              data: {},
            })
          }
        })
      })
    } else {
      updatedEdges = updatedEdges.filter(
        (edge) => edge.source !== node.id && edge.target !== node.id
      )

      outgoers.forEach((outNode) => {
        incomers.forEach((inNode) => {
          const alreadyConnected = updatedEdges.some(
            (e) => e.source === inNode.id && e.target === outNode.id
          )
          if (!alreadyConnected) {
            const originalIncomingEdge = edges.find(
              (e) => e.source === inNode.id && e.target === node.id
            )

            const originalOutgoingEdge = edges.find(
              (e) => e.source === node.id && e.target === outNode.id
            )

            let preservedData = {}
            if (originalIncomingEdge?.data && Object.keys(originalIncomingEdge.data).length > 0) {
              preservedData = originalIncomingEdge.data
            } else if (
              originalOutgoingEdge?.data &&
              Object.keys(originalOutgoingEdge.data).length > 0
            ) {
              preservedData = originalOutgoingEdge.data
            }

            updatedEdges.push({
              id: `${inNode.id}->${outNode.id}`,
              source: inNode.id,
              target: outNode.id,
              type: EDGE_TYPES.SMOOTHSTEP,
              data: preservedData,
            })
          }
        })
      })
    }
  })

  return {updatedNodes, updatedEdges}
}

/**
 * Hook for deleting React Flow nodes with undo/redo support
 *
 * @param args - Configuration object with all required dependencies
 * @returns Object with handleDelete function and deleteNodesLogic
 */
export function useFlowNodeDelete({
  nodes,
  edges,
  setNodes,
  setEdges,
  dispatch,
  flowName,
  flowStatus,
  setNodesProgrammatic,
  setEdgesProgrammatic,
  pushToUndoStack,
  isUndoRedoOperation,
}: UseFlowNodeDeleteArgs) {
  /**
   * Handle deletion of a single node
   * Saves state to undo stack before deletion and updates the flow
   *
   * @param id - ID of the node to delete
   * @param node - Node object to delete
   * @param e - Optional mouse event to stop propagation
   */
  const handleDelete = useCallback(
    async (id: string, node: Node, e?: React.MouseEvent) => {
      if (e) e.stopPropagation()

      if (node?.data && (node.data as any)?.deletable === false) return

      if (isUndoRedoOperation && isUndoRedoOperation.current) return

      if (node.type === NodeType.CONDITION) {
        const deleteOptions = getConditionDeleteOptions(nodes, edges, id)

        if (deleteOptions.length === 0) {
          await performRegularDelete(node)
          return
        }

        // For condition nodes, the modal will handle the confirmation
        // This function is called when user clicks the delete button
        // The modal will call handleConditionDelete with the selected branch type
        return
      }

      // Regular deletion for non-condition nodes
      await performRegularDelete(node)
    },
    [
      nodes,
      edges,
      setNodes,
      setEdges,
      setNodesProgrammatic,
      setEdgesProgrammatic,
      dispatch,
      flowName,
      flowStatus,
      pushToUndoStack,
      isUndoRedoOperation,
    ]
  )

  /**
   * Perform regular deletion for non-condition nodes
   */
  const performRegularDelete = useCallback(
    async (node: Node) => {
      // Save current state before making changes
      if (pushToUndoStack) {
        pushToUndoStack(nodes, edges)
      }

      // Perform the deletion logic
      const {updatedNodes, updatedEdges} = deleteNodesLogic([node], nodes, edges)

      // Update local state
      setNodes(updatedNodes)
      setEdges(updatedEdges)

      // Update programmatic state if available
      if (setNodesProgrammatic) {
        setNodesProgrammatic(updatedNodes)
      }
      if (setEdgesProgrammatic) {
        setEdgesProgrammatic(updatedEdges)
      }

      // Update Redux state if dispatch is available
      if (dispatch && typeof flowName !== 'undefined' && typeof flowStatus !== 'undefined') {
        dispatch(
          setFlowDraft({
            name: flowName,
            nodes: updatedNodes,
            edges: updatedEdges,
            status: flowStatus,
          })
        )
      }
    },
    [
      nodes,
      edges,
      setNodes,
      setEdges,
      setNodesProgrammatic,
      setEdgesProgrammatic,
      dispatch,
      flowName,
      flowStatus,
      pushToUndoStack,
    ]
  )

  /**
   * Handle condition node deletion with branch selection
   */
  const handleConditionDelete = useCallback(
    async (id: string, branchType: BranchType) => {
      // Prevent operation during undo/redo to avoid infinite loops
      if (isUndoRedoOperation && isUndoRedoOperation.current) return

      // Save current state before making changes
      if (pushToUndoStack) {
        pushToUndoStack(nodes, edges)
      }

      // Perform condition-specific deletion
      const result = deleteConditionBranch({
        nodes,
        edges,
        conditionNodeId: id,
        branchType,
      })

      // Update states
      setNodes(result.updatedNodes)
      setEdges(result.updatedEdges)

      if (setNodesProgrammatic) {
        setNodesProgrammatic(result.updatedNodes)
      }
      if (setEdgesProgrammatic) {
        setEdgesProgrammatic(result.updatedEdges)
      }

      // Update Redux state if dispatch is available
      if (dispatch && typeof flowName !== 'undefined' && typeof flowStatus !== 'undefined') {
        dispatch(
          setFlowDraft({
            name: flowName,
            nodes: result.updatedNodes,
            edges: result.updatedEdges,
            status: flowStatus,
          })
        )
      }
    },
    [
      nodes,
      edges,
      setNodes,
      setEdges,
      setNodesProgrammatic,
      setEdgesProgrammatic,
      dispatch,
      flowName,
      flowStatus,
      pushToUndoStack,
      isUndoRedoOperation,
    ]
  )

  return {handleDelete, handleConditionDelete, deleteNodesLogic}
}
